import apiClient from "@/services/api/apiClient";
import {
  Apple,
  CheckCircle2,
  CloudSun,
  Download,
  Link as LinkIcon,
  Loader2,
  RefreshCw,
  Unplug,
  Upload,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";

type ImportResult = {
  imported: number;
  weight: number;
  height: number;
  heartRate: number;
  bloodPressure: number;
  bodyFat: number;
  steps: number;
};

type GoogleStatus = {
  configured: boolean;
  connected: boolean;
  lastSyncAt: string | null;
  scope: string | null;
};

interface Props {
  onImported?: () => Promise<void> | void;
}

export const HealthEcosystemImportBox: React.FC<Props> = ({ onImported }) => {
  const location = useLocation();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [appleLoading, setAppleLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<ImportResult | null>(null);
  const [google, setGoogle] = useState<GoogleStatus>({
    configured: false,
    connected: false,
    lastSyncAt: null,
    scope: null,
  });

  const loadGoogleStatus = async () => {
    try {
      const res = await apiClient.get("/health-import/google/status");
      setGoogle(res.data.data);
    } catch {
      // keep silent
    }
  };

  useEffect(() => {
    loadGoogleStatus();
  }, []);

  useEffect(() => {
    const state = new URLSearchParams(location.search).get("googleFit");
    if (state === "connected") {
      setMessage(t.ecoImportGoogleConnected);
      setError("");
      loadGoogleStatus();
    } else if (state === "error" || state === "invalid") {
      setError(t.ecoImportGoogleConnectFailed);
    }
    // Only the URL decides when this runs; a language switch must not re-show it.
  }, [location.search]);

  const handleAppleClick = () => fileRef.current?.click();

  const handleAppleImport = async (file: File) => {
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xml")) {
      setError(t.ecoImportAppleWrongFile);
      return;
    }

    setAppleLoading(true);
    setMessage("");
    setError("");
    setSummary(null);

    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiClient.post("/health-import/apple/import", form);
      setSummary(res.data.data);
      setMessage(t.ecoImportAppleSuccess);
      if (onImported) await onImported();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t.ecoImportAppleFailed;
      setError(msg);
    } finally {
      setAppleLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    setMessage("");
    setError("");
    try {
      const res = await apiClient.get("/health-import/google/auth-url");
      const url = res.data.data?.authUrl;
      if (url) window.location.href = url;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t.ecoImportGoogleNotConfigured;
      setError(msg);
      setGoogleLoading(false);
    }
  };

  const handleGoogleSync = async () => {
    setGoogleLoading(true);
    setMessage("");
    setError("");
    setSummary(null);
    try {
      const res = await apiClient.post("/health-import/google/sync");
      setSummary(res.data.data);
      setMessage(t.ecoImportGoogleSyncSuccess);
      await loadGoogleStatus();
      if (onImported) await onImported();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t.ecoImportGoogleSyncFailed;
      setError(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setMessage("");
    setError("");
    try {
      await apiClient.post("/health-import/google/disconnect");
      setGoogle({
        configured: google.configured,
        connected: false,
        lastSyncAt: null,
        scope: null,
      });
      setMessage(t.ecoImportGoogleDisconnected);
    } catch {
      setError(t.ecoImportGoogleDisconnectFailed);
    } finally {
      setDisconnecting(false);
    }
  };

  const lastSync = (iso: string | null) =>
    iso ? fmt.dateTime(iso, "medium") : t.never;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {t.ecoImportTitle}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{t.ecoImportSubtitle}</p>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-xs text-primary-600 bg-primary-50 rounded-full px-3 py-1.5">
          <Download className="w-3.5 h-3.5" />
          {t.ecoImportNoAppRequired}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Apple Health */}
        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
              <Apple className="w-5 h-5 text-gray-700" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                Apple Health
              </div>
              <div className="text-xs text-gray-500">
                {t.ecoImportAppleSubtitle}
              </div>
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".xml,text/xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleAppleImport(file);
            }}
          />

          <button
            onClick={handleAppleClick}
            disabled={appleLoading}
            className="w-full py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {appleLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {t.ecoImportAppleButton}
          </button>
          <p className="text-[11px] text-gray-400">{t.ecoImportAppleHint}</p>
        </div>

        {/* Google Fit */}
        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
              <CloudSun className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                Google Fit
              </div>
              <div className="text-xs text-gray-500">
                {google.connected
                  ? t.ecoImportGoogleStatusConnected.replace(
                      "{time}",
                      lastSync(google.lastSyncAt),
                    )
                  : t.ecoImportGoogleConnectPrompt}
              </div>
            </div>
          </div>

          {!google.connected ? (
            <button
              onClick={handleGoogleConnect}
              disabled={googleLoading}
              className="w-full py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {googleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LinkIcon className="w-4 h-4" />
              )}
              {t.ecoImportGoogleConnect}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleGoogleSync}
                disabled={googleLoading}
                className="flex-1 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {t.ecoImportSyncNow}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                title={t.ecoImportGoogleDisconnect}
                aria-label={t.ecoImportGoogleDisconnect}
              >
                {disconnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Unplug className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {(message || error) && (
        <div
          className={`mt-4 rounded-lg px-4 py-3 text-sm ${
            error
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {error || message}
        </div>
      )}

      {summary && (
        <div className="mt-4 rounded-xl border border-primary-100 bg-primary-50 p-4">
          <div className="flex items-center gap-2 text-primary-800 mb-3">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-semibold text-sm">{t.ecoImportSummary}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat
              label={t.ecoImportStatTotal}
              value={fmt.number(summary.imported)}
            />
            <Stat label={t.weight} value={fmt.number(summary.weight)} />
            <Stat label={t.height} value={fmt.number(summary.height)} />
            <Stat label={t.heartRate} value={fmt.number(summary.heartRate)} />
            <Stat
              label={t.bloodPressure}
              value={fmt.number(summary.bloodPressure)}
            />
            <Stat label={t.bodyFat} value={fmt.number(summary.bodyFat)} />
            <Stat
              label={t.ecoImportStatSteps}
              value={fmt.number(summary.steps)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white rounded-lg px-3 py-2 border border-primary-100">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-bold text-gray-900">{value}</div>
  </div>
);
